<template lang="pug">
academy-list.academy-management(
  ref='template',
  academyCodePlaceholder='塾コード',
  academyNamePlaceholder='塾名',
  modalTitle='塾登録',
  modalAcademyNamePlaceholder='塾名',
  modalAcademyCodePlaceholder='塾コード',
  :userRole='userRole',
  :currentLink='currentLink',
  :sideMenuLinkEvent='sideMenuLinkEvent',
  :logoutEvent='logoutEvent',
  :breadcrumbs='breadcrumbs',
  :setRegisterDataEvent='setRegisterDataEvent',
  :tabHeadingEvent='tabHeadingEvent',
  :searchEvent='searchEvent',
  :downloadEvent='downloadEvent',
  :uploadEvent='uploadEvent',
  :sort='sort',
  :academyDatas='academyDatas',
  :deleteEvent='deleteEvent',
  :setEditDataEvent='setEditDataEvent',
  :pagenationStatus='pagenationStatus',
  :pagenationEvent='pagenationEvent',
  :academyEditData='academyEditData',
  :modalKeepButtonEvent='modalKeepButtonEvent',
  :modalErrorMessages='modalErrorMessages',
  @sort-changed='sortChanged'
)
  template(#sideMenuSeparateText) 設定
  template(#logoutText) サインアウト
  template(#breadcrumbButtonLabel) 新規登録
  template(#searchButtonText) 絞り込む
  template(#downloadIconLabel) ダウンロード
  template(#uploadIconLabel) アップロード
  template(#academyCodeHeader) 塾コード
  template(#academyNameHeader) 塾名
  template(#updatedDateHeader) 最終更新日
  template(#editHeader) 編集
  template(#popupRemoveText) 削除
  template(#popupEditText) 編集
  template(#modalAcademyNameLabel) 塾名
  template(#modalAcademyCodeLabel) 塾コード
  template(#modalAcademyCodeDisabledLabel) 塾コード（編集不可）
  template(#modalKeepText) 保存
</template>

<script lang="ts">
import { Component, Mixins, Ref } from 'vue-property-decorator'
import LoginMethods from '@/mixins/LoginMethods'
import AcademyList from '@/components/templates/manager/AcademyList.vue'
import AcademiesApi from '@/mixins/api/AcademiesApi'
import { breadcrumbType } from '@/models/breadcrumbModel'
import {
  AcademyListType,
  AcademyListSortType,
  AcademyEditType,
  AcademySearchParamsType,
} from '@/models/academyListModel'
import { PaginationStatus } from '@/models/paginationModel'
import { LinkSetting, SideMenuSettings } from '@/const/SideMenuSettings'
import { UserRoles } from '@/const/UserRoles'
import { SortSettings } from '@/const/SortSettings'
import { AcademyManagementUtils } from '@/utils/AcademyManagementUtils'

/**
 * システム管理者・塾管理画面
 */
@Component({
  components: {
    AcademyList,
  },
})
export default class AcademyManagement extends Mixins(LoginMethods, AcademiesApi) {
  // テンプレートへのアクセス
  @Ref() template!: AcademyList

  // パンくず
  private breadcrumbs: breadcrumbType[] = [
    {
      label: '塾・教室管理',
    },
  ]

  // システム管理者権限
  private userRole: UserRoles = UserRoles.ADMIN

  // サイドメニューの現在リンク
  private currentLink: LinkSetting = SideMenuSettings.MANAGER_ACADEMY

  // ソート情報
  private sort: AcademyListSortType | null = null

  // 塾一覧表示情報
  private academyDatas: AcademyListType[] = []

  // ページネーションの状態
  private pagenationStatus: PaginationStatus | null = null

  // モーダルに表示する塾編集情報
  private academyEditData: AcademyEditType | null = null

  // モーダルに表示するエラーメッセージ
  private modalErrorMessages: string[] = []

  // 現在の検索パラメーター
  private currentSearchParams: AcademySearchParamsType | null = null

  // 現在のページ
  private get currentPage(): number {
    return this.pagenationStatus?.page || 1
  }

  // 初期表示処理
  private async mounted() {
    this.$loading.start()
    // デフォルトソート値をセットして1ページ目を検索する
    this.setDefaultSort()
    await this.search(1)
    this.$loading.complete()
  }

  // デフォルトソート値をセット
  private setDefaultSort() {
    this.sort = {
      academyName: SortSettings.ASC,
      academyCode: SortSettings.ASC,
      updatedDate: SortSettings.ASC,
    }
  }

  // 検索ボタン押下イベント
  private async searchEvent() {
    this.$loading.start()
    // 全チェックボックス、ソートを初期化
    this.template.checkedAll = false
    this.setDefaultSort()
    // 1ページ目を検索
    await this.search(1)
    this.$loading.complete()
  }

  // ページ遷移イベント
  private async pagenationEvent(page: number) {
    this.$loading.start()
    // 指定されたページを検索
    await this.search(page)
    this.$loading.complete()
  }

  // ソート変更時イベント
  private async sortChanged(sort: AcademyListSortType) {
    this.$loading.start()
    // ソートを更新
    this.sort = sort
    // 現在のページを検索
    await this.search(this.currentPage)
    this.$loading.complete()
  }

  // 検索処理
  private async search(page: number) {
    if (!this.sort) {
      return
    }
    // 塾一覧情報検索API呼び出し
    this.currentSearchParams = {
      page: page,
      academyName: this.template.academyName,
      academyCode: this.template.academyCode,
      academyNameSort: this.sort.academyName.code,
      academyCodeSort: this.sort.academyCode.code,
      updatedAtSort: this.sort.updatedDate.code,
    }
    const apiResponse = await this.searchAcademies(this.currentSearchParams)
    // 表示情報に成型する
    this.academyDatas = AcademyManagementUtils.formatAcademyDatas(apiResponse)
    this.pagenationStatus = AcademyManagementUtils.getPagenationStatus(apiResponse)
    // チェックボックスのチェック状態をセット
    this.setCheckedAcademyIds()
  }

  // チェックボックスのチェック状態をセットする処理
  private setCheckedAcademyIds() {
    if (this.template.checkedAll) {
      // 全チェックボックスがチェックされている場合、全データのチェックボックスをチェックする
      this.template.checkedAcademyIds = this.academyDatas.map((academyData) => {
        return academyData.id
      })
    } else {
      // チェックされていない場合は全てを未チェックにする
      this.template.checkedAcademyIds = []
    }
  }

  // ダウンロードイベント
  private async downloadEvent() {
    if (this.currentSearchParams === null) {
      return
    }
    // 対象チェックボックスにチェックがない場合はダウンロードを行わない
    if (this.template.checkedAcademyIds.length === 0) {
      alert('ダウンロード対象をチェックしてください')
      return
    }
    // ダウンロード処理
    this.$loading.start()
    // チェックボックスがチェックされた塾情報を絞り込みに使用する（全選択時は絞り込み不要）
    const academyIds: number[] = this.template.checkedAll ? [] : this.template.checkedAcademyIds
    try {
      // 直近の検索パラメーターを用いてエクセルデータを取得する
      const exportData = await this.exportAcademyExcel(this.currentSearchParams, academyIds)
      // aタグを生成し、hrefにデータを設定してclickを実行することでファイルをダウンロード
      const fileLink = document.createElement('a')
      fileLink.href = URL.createObjectURL(exportData)
      fileLink.download = '塾一覧.xlsx'
      fileLink.click()
      this.$loading.complete()
    } catch {
      alert('エラーが発生しました')
    }
  }

  // アップロードイベント
  private async uploadEvent(file: File) {
    this.$loading.start()
    try {
      await this.importAcademyExcel(file)
      alert('取込みが完了しました')
      // 再検索する
      await this.search(this.currentPage)
      this.$loading.complete()
    } catch (e: any) {
      alert(e.response.data.message.message)
    }
  }

  // サイドメニュー遷移イベント
  private sideMenuLinkEvent(link: LinkSetting) {
    // 現ページのリンク押下時は遷移処理なし
    if (link === this.currentLink) return

    // 設定されたリンク先ページへ遷移
    this.$router.push({ name: `Admin${link.name}` })
  }

  // ログアウトイベント
  private logoutEvent() {
    this.logout()
    // ログインページへ遷移
    this.$router.push({ name: 'AdminLogin' })
  }

  // 見出しタブ押下イベント
  private tabHeadingEvent(name: string) {
    // リンク先ページへ遷移
    this.$router.push({ name: `Admin${name}` })
  }

  // 新規登録情報を設定するイベント
  private setRegisterDataEvent() {
    // エラーメッセージを初期化する
    this.modalErrorMessages = []
    this.academyEditData = AcademyManagementUtils.buildAcademyEditData()
  }

  // 編集データを取得して設定するイベント
  private async setEditDataEvent(id: number) {
    // エラーメッセージを初期化する
    this.modalErrorMessages = []
    // 塾情報取得API呼び出し
    this.$loading.start()
    const apiResponse = await this.getAcademyData(id)
    this.academyEditData = AcademyManagementUtils.buildAcademyEditData(apiResponse)
    this.$loading.complete()
  }

  // モーダルの保存ボタン押下時のイベント
  private async modalKeepButtonEvent(): Promise<void> {
    if (!this.academyEditData) {
      return
    }
    // エラーメッセージを初期化する
    this.modalErrorMessages = []
    this.$loading.start()
    try {
      if (this.academyEditData.id) {
        // academyEditDataにIDがある場合は更新処理
        await this.updateAcademyData(this.academyEditData)
      } else {
        // ない場合は新規登録
        await this.createAcademyData(this.academyEditData)
      }
      // 再検索する
      await this.search(this.currentPage)
      this.$loading.complete()
    } catch (e: any) {
      // エラー発生時はエラーメッセージを設定する
      this.modalErrorMessages = [e.response.data.message]
    }
  }

  // 削除イベント
  private async deleteEvent(id: number): Promise<void> {
    this.$loading.start()
    try {
      await this.deleteAcademyData(id)
      // 再検索する
      await this.search(this.currentPage)
      this.$loading.complete()
    } catch (e: any) {
      // エラー時はエラーメッセージをアラート
      alert(e.response.data.message)
    }
  }
}
</script>
